import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MagnifyingGlass, GridFour, ListBullets, EnvelopeSimple, Phone, Buildings, Stack, UserCircle, Printer, Plus, UploadSimple, Trash, PencilSimple, Key, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { DateInput } from "../components/ui/date-input";

const HR_ROLES = new Set(["super_admin", "company_admin", "country_head", "region_head", "branch_manager"]);

const EMP_CLASS_LABELS = {
  on_roll:              { label: "On-Roll",    cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  off_roll_consultant:  { label: "Consultant", cls: "bg-amber-50 border-amber-200 text-amber-700" },
  off_roll_contractor:  { label: "Contractor", cls: "bg-sky-50 border-sky-200 text-sky-700" },
  intern:               { label: "Intern",     cls: "bg-violet-50 border-violet-200 text-violet-700" },
};

export default function Employees() {
  const { user } = useAuth();
  const canAddEmployee = HR_ROLES.has(user?.role);
  const isSuperAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [deptId, setDeptId] = useState("all");
  const [branchId, setBranchId] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [employmentClass, setEmploymentClass] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("employment_class") || "all";
    } catch { return "all"; }
  });
  const [view, setView] = useState("table"); // table | cards
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  // Confirm-then-delete state. We keep the candidate employee object so the
  // confirm dialog can show "Delete <Name>?" instead of a faceless prompt.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingReset, setPendingReset] = useState(null);
  const [resetMode, setResetMode] = useState("temp");
  const [resetPassword, setResetPassword] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => { const t = setTimeout(() => setQDebounced(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const loadLookups = async () => {
    try {
      const calls = [api.get("/org/departments"), api.get("/org/branches")];
      if (isSuperAdmin) calls.push(api.get("/companies"));
      const [ds, bs, cs] = await Promise.all(calls);
      setDepartments(ds.data); setBranches(bs.data);
      if (cs) setCompanies(cs.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { loadLookups(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (qDebounced) params.q = qDebounced;
      if (type !== "all") params.employee_type = type;
      if (deptId !== "all") params.department_id = deptId;
      if (branchId !== "all") params.branch_id = branchId;
      if (employmentClass !== "all") params.employment_class = employmentClass;
      const emps = await api.get("/employees", { params });
      setRows(emps.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [qDebounced, type, deptId, branchId, employmentClass]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/employees/${pendingDelete.id}`);
      const bits = [`${data.name || pendingDelete.name} removed.`];
      if (data.deleted_user) bits.push(`Email ${data.freed_email} freed.`);
      toast.success(bits.join(" "));
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not delete employee");
    } finally { setDeleting(false); }
  };

  const stats = useMemo(() => {
    const m = { wfo: 0, wfh: 0, field: 0, hybrid: 0 };
    rows.forEach(r => { if (m[r.employee_type] != null) m[r.employee_type]++; });
    return m;
  }, [rows]);

  return (
    <AppShell title="Employee Directory">
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="dir-toolbar">
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input placeholder="Search name, email, code, title…" className="pl-8 h-9" value={q} onChange={e => setQ(e.target.value)} data-testid="dir-search"/>
        </div>
        <Select value={deptId} onValueChange={setDeptId}>
          <SelectTrigger className="w-44 h-9" data-testid="dir-dept"><SelectValue placeholder="Department"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger className="w-44 h-9" data-testid="dir-branch"><SelectValue placeholder="Branch"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.is_head_office ? " · HQ" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-36 h-9" data-testid="dir-type-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modes</SelectItem>
            <SelectItem value="wfo">WFO</SelectItem>
            <SelectItem value="wfh">WFH</SelectItem>
            <SelectItem value="field">Field</SelectItem>
            <SelectItem value="hybrid">Hybrid</SelectItem>
          </SelectContent>
        </Select>
        <Select value={employmentClass} onValueChange={setEmploymentClass}>
          <SelectTrigger className="w-40 h-9" data-testid="dir-employment-class-select">
            <SelectValue placeholder="Employment"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classes</SelectItem>
            <SelectItem value="on_roll">On-Roll</SelectItem>
            <SelectItem value="off_roll_consultant">Consultant</SelectItem>
            <SelectItem value="off_roll_contractor">Contractor</SelectItem>
            <SelectItem value="intern">Intern</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <div className="border border-zinc-200 rounded-md flex">
            <button className={`px-2.5 py-1.5 ${view === "table" ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-900"}`} onClick={() => setView("table")} data-testid="dir-view-table"><ListBullets size={14}/></button>
            <button className={`px-2.5 py-1.5 ${view === "cards" ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-900"}`} onClick={() => setView("cards")} data-testid="dir-view-cards"><GridFour size={14}/></button>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5 h-9" data-testid="dir-print-pdf"
            onClick={async () => {
              try {
                const params = new URLSearchParams();
                if (q) params.set("q", q);
                if (deptId !== "all") params.set("department_id", deptId);
                if (branchId !== "all") params.set("branch_id", branchId);
                if (type !== "all") params.set("employee_type", type);
                const res = await api.get("/employees/directory-pdf?" + params.toString(), { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url; a.download = "employee_directory.pdf";
                document.body.appendChild(a); a.click(); a.remove();
                toast.success("Directory PDF generated");
              } catch (err) { toast.error("Print failed"); }
            }}>
            <Printer size={14}/> Print PDF
          </Button>
          {canAddEmployee && (
            <Link to="/app/bulk-import">
              <Button size="sm" variant="outline" className="gap-1.5 h-9" data-testid="dir-bulk-upload-btn">
                <UploadSimple size={14} weight="bold"/> Bulk employee import
              </Button>
            </Link>
          )}
          {canAddEmployee && (
            <Button size="sm" className="gap-1.5 h-9 bg-zinc-950 hover:bg-zinc-800" onClick={() => setShowAdd(true)} data-testid="dir-add-employee-btn">
              <Plus size={14} weight="bold"/> Add employee
            </Button>
          )}
        </div>
      </div>

      <SectionCard title={`${rows.length} ${rows.length === 1 ? "person" : "people"}`}
        subtitle={loading ? "Loading…" : `WFO ${stats.wfo} · WFH ${stats.wfh} · Field ${stats.field} · Hybrid ${stats.hybrid}`}
        testid="section-directory"
      >
        {view === "table" ? <TableView rows={rows} canManage={canAddEmployee} onDelete={setPendingDelete} onResetPassword={setPendingReset} currentUserEmpId={user?.employee_id}/> : <CardsView rows={rows} canManage={canAddEmployee} onDelete={setPendingDelete} currentUserEmpId={user?.employee_id}/>}
      </SectionCard>
      {showAdd && <AddEmployeeModal isSuperAdmin={isSuperAdmin} companies={companies} departments={departments} branches={branches} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }}/>}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="emp-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this employee?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <div>
                <strong>{pendingDelete?.name}</strong> ({pendingDelete?.email}) will be removed from the directory.
              </div>
              <div className="text-xs text-zinc-500">
                Their login is deleted and the email is freed for re-use. Their employee code is retired (never reused), open onboarding is closed, and historical attendance, leave and payslip records stay linked to them.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="emp-delete-cancel" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="emp-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Deleting…" : "Delete employee"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password dialog */}
      <ResetPasswordDialog
        emp={pendingReset}
        mode={resetMode} setMode={setResetMode}
        password={resetPassword} setPassword={setResetPassword}
        busy={resetBusy}
        result={resetResult}
        onClose={() => { setPendingReset(null); setResetMode("temp"); setResetPassword(""); setResetResult(null); }}
        onConfirm={async () => {
          if (!pendingReset) return;
          setResetBusy(true); setResetResult(null);
          try {
            const payload = { mode: resetMode };
            if (resetMode === "temp" && resetPassword) payload.new_password = resetPassword;
            const { data } = await api.post(`/employees/${pendingReset.id}/reset-password`, payload);
            setResetResult(data);
          } catch (e) {
            setResetResult({ error: e?.response?.data?.detail || "Reset failed" });
          } finally { setResetBusy(false); }
        }}
      />
    </AppShell>
  );
}

function randomTempPassword() {
  const a = "ABCDEFGHJKLMNPQRSTUVWXYZ", b = "abcdefghijkmnpqrstuvwxyz", d = "23456789", sp = "@#$%&!";
  const pick = (set, n) => Array.from(crypto.getRandomValues(new Uint32Array(n)), x => set[x % set.length]).join("");
  return pick(a, 2) + pick(b, 5) + pick(d, 3) + pick(sp, 1);
}

function AddEmployeeModal({ isSuperAdmin, companies, departments, branches, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", email: "", phone: "", job_title: "",
    employee_code: "",
    employee_type: "wfo", employment_class: "on_roll",
    department_id: "", branch_id: "",
    role_in_company: "employee",
    create_login: true, password: randomTempPassword(),
    company_id: "",
    // First-day kit — mirror Pydantic defaults so the modal checkboxes
    // render CHECKED on open (previous bug: undefined → unchecked → user
    // thought emails were off even though backend was firing them).
    date_of_joining: "",
    auto_start_onboarding: true,
    send_welcome_email: true,
  });
  const [busy, setBusy] = useState(false);
  // Holds the post-success summary for the in-modal celebration card.
  // When set, the modal renders a big green success state for ~1.6s before
  // auto-closing — guarantees the user can't miss the confirmation even if
  // they ignore the toast.
  const [successInfo, setSuccessInfo] = useState(null);
  // Holds the error message for the in-modal red error card. Persists until
  // the user clicks "Try again" — no auto-dismiss so they can't miss it.
  const [errorMsg, setErrorMsg] = useState(null);
  const [dupExistingId, setDupExistingId] = useState(null);
  // For super_admin: depts/branches need to be scoped to the chosen company.
  const [scopedDepts, setScopedDepts] = useState([]);
  const [scopedBranches, setScopedBranches] = useState([]);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  // When super_admin picks a company, fetch that company's branches + departments.
  useEffect(() => {
    if (!isSuperAdmin || !f.company_id) {
      setScopedDepts([]); setScopedBranches([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/org/tree", { params: { company_id: f.company_id } });
        if (cancelled) return;
        const tree = r.data || {};
        const allBranches = [];
        const allDepts = [];
        (tree.regions || []).forEach(reg => (reg.countries || []).forEach(co => (co.branches || []).forEach(b => {
          allBranches.push(b);
          (b.departments || []).forEach(d => allDepts.push(d));
        })));
        setScopedBranches(allBranches);
        setScopedDepts(allDepts);
      } catch {
        setScopedDepts([]); setScopedBranches([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin, f.company_id]);

  // Effective dropdown lists.
  const deptOptions = isSuperAdmin ? scopedDepts : departments;
  const branchOptions = isSuperAdmin ? scopedBranches : branches;

  const submit = async () => {
    const fail = (m) => { setErrorMsg(m); toast.error(m); };
    if (!f.name.trim() || !f.job_title.trim()) {
      return fail("Name and job title are required");
    }
    if (f.phone.trim() && !/^\+?\d[\d\s-]{6,15}$/.test(f.phone.trim())) {
      return fail("Phone must contain only digits (optionally +country code)");
    }
    if (f.employee_code.trim() && !/^[A-Za-z0-9][A-Za-z0-9._/-]{1,31}$/.test(f.employee_code.trim())) {
      return fail("Employee code: 2-32 letters, digits, dash, underscore, dot or slash — no spaces or symbols");
    }
    // Email is OPTIONAL. If provided, still enforce a basic format check so
    // typos don't slip through. Blue-collar / field / contractor hires may
    // legitimately have no work email.
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      return fail("Email format looks invalid — leave blank if the employee has no work email");
    }
    if (f.create_login && !f.email.trim()) {
      return fail("Cannot auto-create a login without an email — either add an email or turn off 'Create login'");
    }
    if (isSuperAdmin && !f.company_id) {
      return fail("Please pick a company to add this employee to");
    }
    setBusy(true);
    setErrorMsg(null);  // clear any prior error
    setDupExistingId(null);
    try {
      const payload = { ...f };
      if (!payload.department_id) delete payload.department_id;
      if (!payload.branch_id) delete payload.branch_id;
      if (!payload.create_login) delete payload.password;
      if (!payload.date_of_joining) delete payload.date_of_joining;
      if (!(payload.employee_code || "").trim()) delete payload.employee_code;
      if (!isSuperAdmin) delete payload.company_id;
      const { data: res } = await api.post("/employees", payload);
      const parts = [`${f.name} (${f.email}) added.`];
      if (res?.onboarding_id) parts.push("Onboarding started.");
      if (res?.welcome_email_sent) parts.push(`Magic-link email sent to ${f.email}.`);
      else if (f.send_welcome_email) parts.push("⚠️ Welcome email NOT sent — check Resend domain + backend logs.");
      if (res?.default_template_missing) parts.push("(No default onboarding template — skipped.)");
      toast.success(parts.join(" "), { duration: 8000 });
      // Show in-modal success card before closing — impossible to miss.
      setSuccessInfo({
        name: f.name,
        email: f.email,
        employee_code: res?.employee_code,
        email_sent: !!res?.welcome_email_sent,
        onboarding_started: !!res?.onboarding_id,
      });
      setBusy(false);
      setTimeout(() => onCreated?.(), 1800);
      return;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Structured duplicate response — surface the existing employee's link
      // so the user can jump straight to that row instead of hunting the
      // directory (fix for "profile not present in the list but still says
      // duplicate").
      if (detail && typeof detail === "object" && detail.code === "duplicate_email" && detail.existing_id) {
        setDupExistingId(detail.existing_id);
        setErrorMsg(detail.message);
        toast.error(detail.message, {
          duration: 15000,
          action: {
            label: "View profile",
            onClick: () => { window.location.href = `/app/employees/${detail.existing_id}`; },
          },
        });
        return;
      }
      const msg = formatApiError(detail) || "Failed to add employee";
      setErrorMsg(msg);
      toast.error(msg, { duration: 10000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="dir-add-modal">
        <DialogHeader><DialogTitle>Add a new employee</DialogTitle></DialogHeader>
        {successInfo ? (
          <div className="py-10 text-center" data-testid="dir-add-success-card">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5"/>              </svg>
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-1">
              {successInfo.name} added successfully
            </h3>
            {successInfo.employee_code && (
              <div className="text-xs font-mono text-zinc-500 mb-3">{successInfo.employee_code}</div>
            )}
            <div className="text-sm text-zinc-600 max-w-md mx-auto space-y-1">
              {successInfo.onboarding_started && (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-emerald-600">✓</span> Onboarding (12 tasks) auto-started
                </div>
              )}
              {successInfo.email_sent ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-emerald-600">✓</span> Magic-link email sent to <strong>{successInfo.email}</strong>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-amber-700">
                  <span>⚠</span> Welcome email not sent — check Resend logs
                </div>
              )}
            </div>
            <div className="text-[11px] text-zinc-400 mt-5">Closing in a moment…</div>
          </div>
        ) : (
        <>
        {errorMsg && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="dir-add-error-card">
            <div className="flex items-start gap-2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div className="flex-1">
                <div className="text-sm font-semibold text-red-900 mb-1">Couldn&apos;t add this employee</div>
                <div className="text-sm text-red-800 leading-relaxed">{errorMsg}</div>
                {dupExistingId && (
                  <a
                    href={`/app/employees/${dupExistingId}`}
                    className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-red-900 underline underline-offset-2 hover:text-red-700"
                    data-testid="dir-add-view-existing"
                  >
                    → Open the existing profile
                  </a>
                )}
              </div>
              <button type="button" onClick={() => { setErrorMsg(null); setDupExistingId(null); }} className="text-red-400 hover:text-red-700" aria-label="Dismiss" data-testid="dir-add-error-dismiss">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
          {isSuperAdmin && (
            <div className="col-span-2">
              <Field label="Company *">
                <Select value={f.company_id || ""} onValueChange={v => set("company_id", v)}>
                  <SelectTrigger data-testid="dir-add-company"><SelectValue placeholder="Select a tenant"/></SelectTrigger>
                  <SelectContent>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    {!companies.length && <SelectItem value="_none" disabled>No companies available</SelectItem>}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}
          <Field label="Full name *"><Input value={f.name} onChange={e => set("name", e.target.value)} placeholder="Riya Sharma" data-testid="dir-add-name"/></Field>
          <Field label="Work email"><Input type="email" value={f.email} onChange={e => set("email", e.target.value)} placeholder="riya@acme.io (optional — leave blank if no work email)" data-testid="dir-add-email"/></Field>
          <Field label="Phone"><Input value={f.phone} onChange={e => set("phone", e.target.value)} placeholder="+91…" data-testid="dir-add-phone"/></Field>
          <Field label="Job title *"><Input value={f.job_title} onChange={e => set("job_title", e.target.value)} placeholder="Senior Engineer" data-testid="dir-add-title"/></Field>
          <div className="col-span-2">
            <Field label="Employee code (optional)">
              <Input
                value={f.employee_code}
                onChange={e => set("employee_code", e.target.value)}
                placeholder="Leave blank to auto-generate (EMP-0001…) — or set ACME/2026/QA001"
                data-testid="dir-add-empcode"
                className="font-mono-alt"
              />
              <div className="text-[11px] text-zinc-500 mt-1">Must be unique within your company. 2-32 chars: letters, digits and <code>- _ . /</code>. Leave blank for auto-assign.</div>
            </Field>
          </div>
          <Field label="Department">
            <Select value={f.department_id || "_none"} onValueChange={v => set("department_id", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="dir-add-dept"><SelectValue placeholder="Select"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Unassigned</SelectItem>
                {deptOptions.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Branch / Location">
            <Select value={f.branch_id || "_none"} onValueChange={v => set("branch_id", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="dir-add-branch"><SelectValue placeholder="Select"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Unassigned</SelectItem>
                {branchOptions.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.is_head_office ? " · HQ" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Work mode">
            <Select value={f.employee_type} onValueChange={v => set("employee_type", v)}>
              <SelectTrigger data-testid="dir-add-mode"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="wfo">WFO</SelectItem>
                <SelectItem value="wfh">WFH</SelectItem>
                <SelectItem value="field">Field</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Employment class">
            <Select value={f.employment_class} onValueChange={v => set("employment_class", v)}>
              <SelectTrigger data-testid="dir-add-class"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="on_roll">On-Roll</SelectItem>
                <SelectItem value="off_roll_consultant">Consultant</SelectItem>
                <SelectItem value="off_roll_contractor">Contractor</SelectItem>
                <SelectItem value="intern">Intern</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Role in company">
            <Select value={f.role_in_company} onValueChange={v => set("role_in_company", v)}>
              <SelectTrigger data-testid="dir-add-role"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="branch_manager">Branch manager</SelectItem>
                <SelectItem value="sub_manager">Sub manager</SelectItem>
                <SelectItem value="assistant_manager">Assistant manager</SelectItem>
                <SelectItem value="region_head">Region head</SelectItem>
                <SelectItem value="country_head">Country head</SelectItem>
                <SelectItem value="company_admin">Company admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Temporary password">
            <Input type="text" value={f.password} onChange={e => set("password", e.target.value)} disabled={!f.create_login} data-testid="dir-add-pwd"/>
            <label className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500">
              <input type="checkbox" checked={f.create_login} onChange={e => set("create_login", e.target.checked)} data-testid="dir-add-create-login"/>
              Auto-create login for this employee
            </label>
          </Field>
          <Field label="Date of joining">
            <DateInput value={f.date_of_joining} onChange={e => set("date_of_joining", e.target.value)} data-testid="dir-add-doj"/>
            <div className="mt-1 text-xs text-zinc-500">Used to schedule onboarding task due-dates. Defaults to today if blank.</div>
          </Field>
          <div className="col-span-2 border-t border-zinc-200 pt-3 mt-1">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">First-day kit</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm border border-zinc-200 rounded p-2.5 cursor-pointer">
                <input type="checkbox" checked={f.auto_start_onboarding} onChange={e => set("auto_start_onboarding", e.target.checked)} data-testid="dir-add-auto-onboarding"/>
                <div>
                  <div className="font-medium">Auto-start onboarding</div>
                  <div className="text-xs text-zinc-500">Kick off the default 12-task onboarding immediately.</div>
                </div>
              </label>
              <label className="flex items-center gap-2 text-sm border border-zinc-200 rounded p-2.5 cursor-pointer">
                <input type="checkbox" checked={f.send_welcome_email} onChange={e => set("send_welcome_email", e.target.checked)} data-testid="dir-add-welcome-email"/>
                <div>
                  <div className="font-medium">Email onboarding magic link</div>
                  <div className="text-xs text-zinc-500">New hire fills KYC, banking, education via a self-service wizard — no password needed.</div>
                </div>
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          {busy && (
            <div className="flex items-center gap-2 text-xs text-zinc-500 mr-auto" data-testid="dir-add-saving-indicator">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin"></span>
              Saving employee…
            </div>
          )}
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-zinc-950 hover:bg-zinc-800" onClick={submit} disabled={busy} data-testid="dir-add-submit">
            {busy ? (
              <span className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-white/40 border-t-white animate-spin"></span>
                Adding…
              </span>
            ) : "Add employee"}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="tiny-label mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function TableView({ rows, canManage, onDelete, onResetPassword, currentUserEmpId }) {
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-zinc-200">
        <tr className="text-left text-xs uppercase text-zinc-500">
          <th className="py-2 px-2">Employee</th><th>Job</th><th>Department</th><th>Branch</th>
          <th>Mode</th><th>Class</th><th>Manager</th><th>Status</th>
          {canManage && <th className="text-right pr-2">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={canManage ? 9 : 8} className="text-center py-12 text-zinc-500">No employees match.</td></tr>}
        {rows.map(e => {
          const ec = EMP_CLASS_LABELS[e.employment_class] || EMP_CLASS_LABELS.on_roll;
          const isSelf = currentUserEmpId && currentUserEmpId === e.id;
          return (
          <tr key={e.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`emp-row-${e.id}`}>
            <td className="py-2 px-2">
              <Link to={`/app/employees/${e.id}`} className="flex items-center gap-2.5 group" data-testid={`emp-profile-link-${e.id}`}>
                <Avatar emp={e}/>
                <div>
                  <div className="font-medium group-hover:underline">{e.name}</div>
                  <div className="text-xs text-zinc-500">{e.employee_code} · {e.email}</div>
                </div>
              </Link>
            </td>
            <td className="text-zinc-700">{e.job_title}</td>
            <td className="text-zinc-700 text-xs">{e.department_name || "—"}</td>
            <td className="text-zinc-700 text-xs">{e.branch_name || "—"}</td>
            <td><Badge variant="outline" className="uppercase text-[10px]">{e.employee_type}</Badge></td>
            <td><Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${ec.cls}`} data-testid={`emp-class-${e.id}`}>{ec.label}</Badge></td>
            <td className="text-xs text-zinc-500">{e.manager_id ? <ManagerCell id={e.manager_id}/> : "—"}</td>
            <td>
              <span className="text-[10px] uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full capitalize">{e.status}</span>
            </td>
            {canManage && (
              <td className="text-right pr-2 whitespace-nowrap">
                <Link to={`/app/employees/${e.id}`} title="Edit profile">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`emp-edit-${e.id}`}>
                    <PencilSimple size={13}/>
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => onResetPassword?.(e)}
                  title="Reset password"
                  data-testid={`emp-reset-${e.id}`}
                >
                  <Key size={13}/>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-30"
                  disabled={isSelf}
                  onClick={() => onDelete?.(e)}
                  title={isSelf ? "You can't delete your own record" : "Delete employee"}
                  data-testid={`emp-delete-${e.id}`}
                >
                  <Trash size={13}/>
                </Button>
              </td>
            )}
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ManagerCell({ id }) {
  // Lightweight: use cache to avoid N requests in render
  const cache = ManagerCell.cache || (ManagerCell.cache = new Map());
  const [name, setName] = useState(cache.get(id));
  useEffect(() => {
    if (cache.has(id)) return;
    api.get(`/employees/${id}`).then(r => { cache.set(id, r.data.name); setName(r.data.name); }).catch(()=>{ cache.set(id, "—"); setName("—"); });
    // eslint-disable-next-line
  }, [id]);
  return <span>{name || "…"}</span>;
}

function CardsView({ rows, canManage, onDelete, currentUserEmpId }) {
  if (rows.length === 0) return <p className="text-center py-12 text-zinc-500 text-sm">No employees match.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {rows.map(e => {
        const ec = EMP_CLASS_LABELS[e.employment_class] || EMP_CLASS_LABELS.on_roll;
        const isSelf = currentUserEmpId && currentUserEmpId === e.id;
        return (
        <div key={e.id} className="border border-zinc-200 rounded-lg p-4 hover:border-zinc-400 hover:shadow-sm transition-all group relative" data-testid={`emp-card-${e.id}`}>
          {canManage && (
            <button
              onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); if (!isSelf) onDelete?.(e); }}
              disabled={isSelf}
              className="absolute top-2 right-2 p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
              title={isSelf ? "You can't delete yourself" : "Delete employee"}
              data-testid={`emp-card-delete-${e.id}`}
            >
              <Trash size={13}/>
            </button>
          )}
          <Link to={`/app/employees/${e.id}`} className="block">
            <div className="flex items-center gap-3 mb-3">
              <Avatar emp={e} large/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate group-hover:underline">{e.name}</div>
                <div className="text-xs text-zinc-500 truncate">{e.job_title}</div>
              </div>
            </div>
            <div className="space-y-1.5 text-xs text-zinc-600">
              {e.email && <div className="flex items-center gap-1.5 truncate"><EnvelopeSimple size={12}/>{e.email}</div>}
              {e.phone && <div className="flex items-center gap-1.5"><Phone size={12}/>{e.phone}</div>}
              {e.department_name && <div className="flex items-center gap-1.5"><Stack size={12}/>{e.department_name}</div>}
              {e.branch_name && <div className="flex items-center gap-1.5"><Buildings size={12}/>{e.branch_name}</div>}
            </div>
            <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-zinc-100">
              <Badge variant="outline" className="text-[10px] uppercase">{e.employee_type}</Badge>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${ec.cls}`} data-testid={`emp-class-${e.id}`}>{ec.label}</Badge>
              <Badge variant="outline" className="text-[10px] font-mono-alt">{e.employee_code}</Badge>
            </div>
          </Link>
        </div>
        );
      })}
    </div>
  );
}

function Avatar({ emp, large }) {
  const initials = (emp.name || "").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const colors = ["bg-violet-100 text-violet-700", "bg-emerald-100 text-emerald-700",
    "bg-amber-100 text-amber-700", "bg-sky-100 text-sky-700", "bg-rose-100 text-rose-700"];
  const idx = (emp.name || "").length % colors.length;
  if (emp.avatar_url) return <img src={emp.avatar_url} alt={emp.name} className={`${large ? "w-12 h-12" : "w-8 h-8"} rounded-full object-cover flex-none`}/>;
  return <div className={`${large ? "w-12 h-12 text-sm" : "w-8 h-8 text-[10px]"} rounded-full flex items-center justify-center font-bold flex-none ${colors[idx]}`}>{initials}</div>;
}


// ─── Reset Password Dialog ─────────────────────────────────────────────
function ResetPasswordDialog({ emp, mode, setMode, password, setPassword, busy, result, onClose, onConfirm }) {
  const open = !!emp;
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied to clipboard"); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="emp-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key size={18} weight="bold"/> Reset password
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-sm text-zinc-600">
            Reset the login password for <b>{emp?.name}</b> <span className="text-zinc-400">({emp?.email})</span>.
          </div>

          {!result && (
            <>
              <div className="space-y-2">
                <RadioRow value="temp" checked={mode === "temp"} onSelect={()=>setMode("temp")}
                  title="Set a temporary password" desc="Employee is forced to change it on next login. Instant, works even without email delivery."/>
                <RadioRow value="link" checked={mode === "link"} onSelect={()=>setMode("link")}
                  title="Generate reset link" desc="Copy the URL and share via WhatsApp/personal channel. Valid 2 hours."/>
                <RadioRow value="email" checked={mode === "email"} onSelect={()=>setMode("email")}
                  title="Send reset email" desc="Uses the standard forgot-password flow. Delivery depends on your DNS records."/>
              </div>

              {mode === "temp" && (
                <div>
                  <label className="text-xs text-zinc-500">Password (leave blank to auto-generate)</label>
                  <Input type="text" value={password} onChange={e=>setPassword(e.target.value)}
                    placeholder="Optional — auto-generated if blank" data-testid="emp-reset-pw"/>
                </div>
              )}
            </>
          )}

          {result?.error && (
            <div className="text-sm bg-red-50 border border-red-200 rounded p-3 text-red-700">{result.error}</div>
          )}

          {result?.ok && result.mode === "temp" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2">
              <div className="text-xs font-medium text-emerald-900 uppercase tracking-wide">Temporary password</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-emerald-200 px-3 py-2 rounded font-mono text-sm select-all" data-testid="emp-reset-temp">{result.temporary_password}</code>
                <Button size="sm" variant="outline" onClick={()=>copy(result.temporary_password)} className="gap-1.5"><Copy size={12}/> Copy</Button>
              </div>
              <div className="text-xs text-emerald-800">{result.message}</div>
            </div>
          )}

          {result?.ok && result.mode === "link" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2">
              <div className="text-xs font-medium text-emerald-900 uppercase tracking-wide">Reset link (valid 2 hours)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-emerald-200 px-3 py-2 rounded font-mono text-[11px] break-all select-all" data-testid="emp-reset-link">{result.reset_url}</code>
                <Button size="sm" variant="outline" onClick={()=>copy(result.reset_url)} className="gap-1.5"><Copy size={12}/> Copy</Button>
              </div>
              <div className="text-xs text-emerald-800">{result.message}</div>
            </div>
          )}

          {result?.ok && result.mode === "email" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">{result.message}</div>
          )}
        </div>
        <DialogFooter>
          {!result && <Button variant="outline" onClick={onClose}>Cancel</Button>}
          {!result && <Button onClick={onConfirm} disabled={busy} data-testid="emp-reset-confirm">{busy ? "Working…" : "Reset password"}</Button>}
          {result && <Button onClick={onClose} data-testid="emp-reset-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RadioRow({ checked, onSelect, title, desc }) {
  return (
    <button type="button" onClick={onSelect}
      className={`w-full text-left border rounded-md p-3 flex gap-3 transition ${checked?"border-zinc-950 bg-zinc-50":"border-zinc-200 hover:border-zinc-300"}`}>
      <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${checked?"border-zinc-950 bg-zinc-950":"border-zinc-300"}`}>
        {checked && <div className="w-1.5 h-1.5 bg-white rounded-full mx-auto mt-[3px]"/>}
      </div>
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
      </div>
    </button>
  );
}

